import React, { useEffect, useMemo, useState } from "react";

// region Imports - External libraries
import * as dateFns from "date-fns";
import Loading from "react-fullscreen-loading";
// endregion Imports - External libraries
// region Imports - Shared
import { Vehicle } from "@shared/interfaces/vehicle.interface";
import { IParamQueryValues } from "@shared/interfaces/filter.interface";
import { FilterApplicationTypesID } from "@shared/constants/filter-application-types.enum";
import { Regional } from "@shared/entities/reg_regionals.entity";
import { Group } from "@shared/entities/reg_groups.entity";
import { Plant } from "@shared/entities/reg_plants.entity";
import { VehicleType } from "@shared/interfaces/vehicle-type.interface";
import { ParamNameTypes } from "@shared/constants/param-name-types.enum";
import { Driver } from "@shared/entities/reg_drivers.entity";
// endregion Imports - Shared
// region Imports - Languages
import useTranslation from "src/translations/useTranslation";
import { FilterMessages, MenuMessages, ToastMessages } from "@shared/languages/interfaces";
// endregion Imports - Languages
// region Imports - Services
import api from "@services/api";
// endregion Imports - Services
// region Imports - Utils
import utils from "@utils/useful-functions";
import { vehicleFilterLabel } from "@utils/vehicle-filter-label";
// endregion Imports - Utils
// region Imports - Hooks
import { useToast } from "@hooks/useToast";
// endregion Imports - Hooks
// region Imports - Organisms
import GenericQueryFilter, {
  IDateRangeOptions,
  IMultipleSelectionOptions,
  IOnFilterReturn
} from "@organisms/GenericQueryFilter";
// endregion Imports - Organisms

import { TravelAnalysisFilter } from "../../types";

// region Interfaces
/** Props for the QueryFilterTravelAnalysis organism. */
interface QueryFilterTravelAnalysisProps {
  open: boolean;
  onClose: () => void;
  onFilter: (filter: TravelAnalysisFilter) => Promise<IOnFilterReturn>;
}
// endregion Interfaces

/**
 * GenericQueryFilter wrapper configured for the Travel Analysis dashboard.
 * Follows the same pattern as `QueryFilterVehicleStatesHistory`.
 */
const QueryFilterTravelAnalysis: React.FC<QueryFilterTravelAnalysisProps> = ({ open, onClose, onFilter }) => {

  // region Hooks
  const { t } = useTranslation();
  const { addToast } = useToast();
  // endregion Hooks
  // region useState
  const [optionsVehicleFilter, setOptionsVehicleFilter] = useState<Vehicle[]>([]);
  const [optionsVehicleTypeFilter, setOptionsVehicleTypeFilter] = useState<VehicleType[]>([]);
  const [optionsDriverFilter, setOptionsDriverFilter] = useState<Driver[]>([]);
  const [optionsPlantFilter, setOptionsPlantFilter] = useState<Plant[]>([]);
  const [optionsRegionalFilter, setOptionsRegionalFilter] = useState<Regional[]>([]);
  const [optionsGroupFilter, setOptionsGroupFilter] = useState<Group[]>([]);

  const [loadingVehicleFilter, setLoadingVehicleFilter] = useState(true);
  const [loadingVehicleTypeFilter, setLoadingVehicleTypeFilter] = useState(true);
  const [loadingDriverFilter, setLoadingDriverFilter] = useState(true);
  const [loadingPlantFilter, setLoadingPlantFilter] = useState(true);
  const [loadingRegionalFilter, setLoadingRegionalFilter] = useState(true);
  const [loadingGroupFilter, setLoadingGroupFilter] = useState(true);

  const [loadingFilter, setLoadingFilter] = useState(false);
  // endregion useState
  // region Constants
  const paramNameStartDate = ParamNameTypes.START_DATE;
  const paramNameEndDate = ParamNameTypes.END_DATE;
  const paramNameVehicle = ParamNameTypes.ID_VEHICLES;
  const paramNameVehicleType = ParamNameTypes.ID_VEHICLE_TYPES;
  const paramNameDriver = ParamNameTypes.ID_DRIVERS;
  const paramNamePlant = ParamNameTypes.ID_PLANTS;
  const paramNameRegional = ParamNameTypes.ID_REGIONALS;
  const paramNameGroups = ParamNameTypes.ID_GROUPS;

  const vehicleFilterOptions: IMultipleSelectionOptions = useMemo(() => ({
    isLoadingValues: loadingVehicleFilter,
    labelName: t(FilterMessages.optionVehicles),
    paramName: paramNameVehicle,
    values: optionsVehicleFilter.map((vehicle) => ({
      value: vehicle.id_vehicle,
      label: vehicleFilterLabel(vehicle)
    }))
  }), [loadingVehicleFilter, optionsVehicleFilter, paramNameVehicle, t]);

  const vehicleTypeFilterOptions: IMultipleSelectionOptions = useMemo(() => ({
    isLoadingValues: loadingVehicleTypeFilter,
    labelName: t(FilterMessages.optionVehicleTypes),
    paramName: paramNameVehicleType,
    values: optionsVehicleTypeFilter.map((vt) => ({
      value: vt.id_vehicle_type!,
      label: vt.description
    }))
  }), [loadingVehicleTypeFilter, optionsVehicleTypeFilter, paramNameVehicleType, t]);

  const driverFilterOptions: IMultipleSelectionOptions = useMemo(() => ({
    isLoadingValues: loadingDriverFilter,
    labelName: t(MenuMessages.drivers),
    paramName: paramNameDriver,
    values: optionsDriverFilter.map((driver) => ({
      value: driver.id_driver!,
      label: `${driver.name} - ${driver.cpf}`
    }))
  }), [loadingDriverFilter, optionsDriverFilter, paramNameDriver, t]);

  const plantFilterOptions: IMultipleSelectionOptions = useMemo(() => ({
    isLoadingValues: loadingPlantFilter,
    labelName: t(FilterMessages.optionPlants),
    paramName: paramNamePlant,
    values: optionsPlantFilter.map((plant) => ({
      value: plant!.location!.id_location!,
      label: plant.name!
    }))
  }), [loadingPlantFilter, optionsPlantFilter, paramNamePlant, t]);

  const regionalFilterOptions: IMultipleSelectionOptions = useMemo(() => ({
    isLoadingValues: loadingRegionalFilter,
    labelName: t(FilterMessages.optionRegionals),
    paramName: paramNameRegional,
    values: optionsRegionalFilter.map((regional) => ({
      value: regional.id_regional!,
      label: `${regional.code} - ${regional.name}`
    }))
  }), [loadingRegionalFilter, optionsRegionalFilter, paramNameRegional, t]);

  const groupFilterOptions: IMultipleSelectionOptions = useMemo(() => ({
    isLoadingValues: loadingGroupFilter,
    labelName: t(FilterMessages.optionGroups),
    paramName: paramNameGroups,
    values: optionsGroupFilter.map((group) => ({
      value: group.id_group!,
      label: `${group.code} - ${group.name}`
    }))
  }), [loadingGroupFilter, optionsGroupFilter, paramNameGroups, t]);

  const defaultStartDate = dateFns.subDays(new Date(), 30);

  defaultStartDate.setHours(0, 0, 0, 0);

  const defaultEndDate = new Date();

  defaultEndDate.setHours(23, 59, 59, 999);

  const dateRangeOptions: IDateRangeOptions = {
    labelName: t(FilterMessages.dateRangeLabel),
    paramNameStartDate,
    paramNameEndDate,
    defaultValues: {
      startDate: utils.convertDateToISOWithTimezone(defaultStartDate),
      endDate: utils.convertDateToISOWithTimezone(defaultEndDate)
    },
    maxRange: "30D"
  };
  // endregion Constants
  // region Functions
  const loadVehicleOptions = async () => {
    try {
      const { data } = await api.get("vehicles/read", { params: { linkedHardware: true, onlyLabel: true } });

      if (data.status === "success") setOptionsVehicleFilter(data.result);
    } catch (error) {
      utils.handleStandardError(error, t, addToast);
    } finally {
      setLoadingVehicleFilter(false);
    }
  };

  const loadVehicleTypeOptions = async () => {
    try {
      const { data } = await api.get("vehicles/types/read", { params: { translate: true } });

      if (data.status === "success") setOptionsVehicleTypeFilter(data.result);
    } catch (error) {
      utils.handleStandardError(error, t, addToast);
    } finally {
      setLoadingVehicleTypeFilter(false);
    }
  };

  const loadDriverOptions = async () => {
    try {
      const { data } = await api.get("drivers/read");

      if (data.status === "success") setOptionsDriverFilter(data.result as Driver[]);
    } catch (error) {
      utils.handleStandardError(error, t, addToast);
    } finally {
      setLoadingDriverFilter(false);
    }
  };

  const loadPlantOptions = async () => {
    try {
      const { data } = await api.get("plants/read", { params: { linkedLocation: true } });

      if (data.status === "success") setOptionsPlantFilter(data.result);
    } catch (error) {
      utils.handleStandardError(error, t, addToast);
    } finally {
      setLoadingPlantFilter(false);
    }
  };

  const loadRegionalOptions = async () => {
    try {
      const { data } = await api.get("regionals/read");

      if (data.status === "success") setOptionsRegionalFilter(data.result as Regional[]);
    } catch (error) {
      utils.handleStandardError(error, t, addToast);
    } finally {
      setLoadingRegionalFilter(false);
    }
  };

  const loadGroupOptions = async () => {
    try {
      const { data } = await api.get("groups/read");

      if (data.status === "success") setOptionsGroupFilter(data.result as Group[]);
    } catch (error) {
      utils.handleStandardError(error, t, addToast);
    } finally {
      setLoadingGroupFilter(false);
    }
  };

  /**
   * Transform `IParamQueryValues[]` from GenericQueryFilter into `TravelAnalysisFilter`
   * and delegate to the parent hook.
   */
  const handleFilter = async (paramsData: IParamQueryValues[]): Promise<IOnFilterReturn> => {
    setLoadingFilter(true);

    try {
      const filter: TravelAnalysisFilter = {};

      for (const param of paramsData) {
        switch (param.paramName) {
        case paramNameStartDate:
          filter.from = param.paramValue as string;
          break;
        case paramNameEndDate:
          filter.to = param.paramValue as string;
          break;
        case paramNameVehicle:
          filter.idVehicles = param.paramValue as string[];
          break;
        case paramNameVehicleType:
          filter.idVehicleTypes = param.paramValue as string[];
          break;
        case paramNameDriver:
          filter.idDrivers = param.paramValue as string[];
          break;
        case paramNamePlant:
          filter.idPlants = param.paramValue as string[];
          break;
        case paramNameRegional:
          filter.idRegionals = param.paramValue as string[];
          break;
        case paramNameGroups:
          filter.idGroups = param.paramValue as string[];
          break;
        default:
          break;
        }
      }

      const result = await onFilter(filter);

      return result;

    } catch (error) {
      utils.handleStandardError(error, t, addToast);

      return { status: "error" } as IOnFilterReturn;
    } finally {
      setLoadingFilter(false);
    }
  };

  // endregion Functions
  // region Effects
  useEffect(() => {
    Promise.all([
      loadVehicleOptions(),
      loadVehicleTypeOptions(),
      loadDriverOptions(),
      loadPlantOptions(),
      loadRegionalOptions(),
      loadGroupOptions()
    ]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // endregion Effects

  return (
    <>
      <Loading loading={loadingFilter} />
      <GenericQueryFilter
        open={open}
        onClose={onClose}
        applicationTypeID={FilterApplicationTypesID.TRAVEL_ANALYSIS}
        onFilter={(paramsData) => handleFilter(paramsData)}
        multipleSelectionOptions={[
          vehicleFilterOptions,
          vehicleTypeFilterOptions,
          driverFilterOptions,
          plantFilterOptions,
          regionalFilterOptions,
          groupFilterOptions
        ]}
        dateRangeOptions={dateRangeOptions}
      />
    </>
  );
};

export default QueryFilterTravelAnalysis;
